// Story 1.22 — TanStack mutation for POST
// /api/v1/creators/profile/image-upload-url.
//
// Mirrors Story 1.21's useMintImageUploadUrl exactly except for the
// endpoint path (and the blob path the server returns, which starts
// with `creator-images/profile/{user_id}/`).

import { useMutation } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type MintProfileImageUploadUrlRequest = {
  filename: string;
  content_length: number;
  content_type: string;
};

export type MintProfileImageUploadUrlResponse = {
  upload_url: string;
  blob_path: string;
  expires_at: string;
  required_headers: {
    "x-ms-blob-type": string;
    "x-ms-blob-content-type": string;
  };
};

export type MintProfileImageUploadUrlError = {
  code: string;
  message: string;
  reason?: string;
};

async function postMint(
  body: MintProfileImageUploadUrlRequest,
  idempotencyKey: string,
): Promise<MintProfileImageUploadUrlResponse> {
  const response = await fetch("/api/v1/creators/profile/image-upload-url", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: MintProfileImageUploadUrlError = {
      code: "PROFILE_IMAGE_UPLOAD_URL_MINT_FAILED",
      message: `Mint failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  const envelope = (await response.json()) as {
    data: MintProfileImageUploadUrlResponse;
  };
  return envelope.data;
}

export function useMintProfileImageUploadUrl() {
  return useMutation<
    MintProfileImageUploadUrlResponse,
    MintProfileImageUploadUrlError,
    MintProfileImageUploadUrlRequest
  >({
    mutationFn: (body) => postMint(body, crypto.randomUUID()),
  });
}
